import type { CollectibleId_Schema } from "@/services/collectibles/collectiblesSchema";
import type { CategoryId } from "@/services/collection/categories";
import { type ApiResponseImageNoId } from "@/services/images";
import type { z } from "zod";
import { type PriceNumericOrEmpty, type PriceNumeric, type PricesKeyed } from "@/services/prices";

export type CollectibleId = z.infer<typeof CollectibleId_Schema>;

export enum CollectibleStatus {
  InCollection = "inCollection",
  NotInCollection = "notInCollection",
}

export type Collectible = {
  id: CollectibleId;
  name: string;
  images: ApiResponseImageNoId[];
  price: PriceNumeric | null;
  prices: HistoricalPrice[];
  parameters: [
    {
      parameter: {
        id: number;
        label: string;
        isWidgetIdentificator?: true;
      };
      option: {
        value: string;
      };
    },
  ];
  status: CollectibleStatus;
  ownedCount: number;
} & (
  | {
      categoryId: Exclude<CategoryId, CategoryId.Pokemon>;
    }
  | {
      categoryId: CategoryId.Pokemon;
      priceVariant: PricesKeyed;
      setData?: {
        name: string;
        release: string;
        symbol: string;
      };
    }
);

export type CollectibleWithPrice = Collectible & {
  price: PriceNumeric;
};

/**
 * Feel free to add more category-specific parameters when adding
 * new category into database section.
 */
export type CollectibleSearchListItem = {
  id: CollectibleId;
  name: string;
  images: ApiResponseImageNoId[];
  price: PriceNumeric | null;
  status: CollectibleStatus;
  ownedCount?: number;
} & (
  | {
      categoryId: CategoryId.VinylRecords;
      catNumber: string;
      interpret: string;
      year: string;
    }
  | {
      categoryId: CategoryId.BoardGames;
      developer: string;
      Language: string; // Uppercase "L" is not a bug - see API (umm...)
      year: string;
    }
  | {
      categoryId: CategoryId.LegoMinifigures;
      number: string;
      pieces: string | null;
    }
  | {
      categoryId: CategoryId.LegoSets;
      number: string;
      year: string;
    }
  | {
      categoryId: CategoryId.Watches;
      ref_no: string;
      brand: string;
      family_model: string;
    }
  | {
      categoryId: CategoryId.Pokemon;
      price: PriceNumericOrEmpty;
      prices: PricesKeyed;
      set: string;
      setData: {
        name: string;
        release: string; // "YYYY-MM" format
        symbol: string; // URL to image
      };
      number: string;
      rarity?: string;
      releaseDate: string; // eg "2024/01/26"
    }
);

export type CollectibleSearchListItemWithPrice = CollectibleSearchListItem & {
  price: PriceNumeric;
};

export type HistoricalPrice = {
  price: PriceNumeric;
  date: string;
};

export type AutocompleteCollectibleItem = {
  id: number;
  name: string;
  identification: string;
  images: ApiResponseImageNoId[];
};
